import { useEffect, useState } from 'react'
import { getVodService } from '@/shared/getContainer/getVodService'
import { EpisodeItemInterface, Season } from '@/types/vod/VodItemDetails'
import { useVODStore } from '@/shared/store'
import { IVODServiceStreamIQ } from '@/shared/types/services/types/vodService/IVodServiceStreamIQ'
import { debounce } from '@mui/material'
import { debounceSpeedShort } from '@/shared/constants'

interface ISeasonAndEpisodes {
    seasonId: string
    episodes: EpisodeItemInterface[]
}

interface UseSeasonsAndEpisodesProps {
    seasons: Season[]
    seasonId: string
    episodeId: string
}

interface SeasonsAndEpisodesHook {
    actualSeason: Season | null
    actualEpisodes: ISeasonAndEpisodes | undefined
    handleSelectSeason: (season: Season) => Promise<void>
}

const { fetchVodEpisodes } = (await getVodService()) as IVODServiceStreamIQ

export const useSeasonsAndEpisodesStreamIQ = ({
    seasons,
    seasonId,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    episodeId,
}: UseSeasonsAndEpisodesProps): SeasonsAndEpisodesHook => {
    const [seasonsAndEpisodesLocal, setSeasonsAndEpisodesLocal] = useState<
        ISeasonAndEpisodes[]
    >([])

    const [actualEpisodes, setActualEpisodes] = useState<
        ISeasonAndEpisodes | undefined
    >()
    const {
        setSeasonsAndEpisodes,
        getSeasonsAndEpisodes,
        actualSeason,
        setActualSeason,
    } = useVODStore()

    const handleSelectSeason = async (season: Season) => {
        const cachedEpisodes = getSeasonsAndEpisodes(season.id) || []
        const episodesMap = new Map(
            cachedEpisodes.map((episode) => [episode.productId, episode])
        )

        const fetchedAll = cachedEpisodes.length > 0

        if (cachedEpisodes.length) {
            setActualEpisodes({
                seasonId: season.id,
                episodes: cachedEpisodes,
            })
        }
        if (!fetchedAll) {
            const episodes = await fetchVodEpisodes(season.productId, season.id)
            episodes.forEach((episode) =>
                episodesMap.set(episode.productId || '', episode)
            )
        }
        const uniqueEpisodes = Array.from(episodesMap.values()).sort(
            (episodeA, episodeB) => episodeA.position - episodeB.position
        )
        if (uniqueEpisodes.length) {
            setSeasonsAndEpisodesLocal((prev) => [
                ...prev.filter((item) => item.seasonId !== season.id),
                {
                    seasonId: season.id,
                    episodes: uniqueEpisodes,
                },
            ])

            setSeasonsAndEpisodes({
                seasonId: season.id,
                episodes: uniqueEpisodes,
            })

            setActualSeason(season)
        }
    }

    useEffect(() => {
        const actualEpisodes = seasonsAndEpisodesLocal.find(
            (season) => season.seasonId === actualSeason?.id
        )

        if (actualEpisodes) {
            setActualEpisodes(actualEpisodes)
        }

        const debouncedHandleSelectSeason = debounce(
            () => handleSelectSeason(actualSeason || seasons[0]),
            debounceSpeedShort
        )

        debouncedHandleSelectSeason()
        return () => {
            debouncedHandleSelectSeason.clear()
        }
    }, [
        seasons?.length,
        seasonId,
        actualSeason?.id,
        seasonsAndEpisodesLocal?.length,
    ])

    return {
        actualSeason,
        handleSelectSeason,
        actualEpisodes,
    }
}
